"use client";

import { useMemo, useState } from "react";
import { usePathname } from "next/navigation";

import PriceToggleIcon from "@/components/icons/price-toggle-icon";
import { formatPrice, isFreePrice } from "@/lib/format-price";
import type { PriceCategory, PriceStory } from "@/lib/prices";
import { regionFromPathname } from "@/lib/region";
import { useMediaQuery } from "@/lib/use-media-query";

export type CategoryCard = {
  uid: string;
  label: string;
  category: PriceCategory;
  footnote?: string;
};

type Row = {
  uuid: string;
  name: string;
  cardPrice: string | undefined;
  isFree: boolean;
};

function buildRow(price: PriceStory, region: "ie" | "uk"): Row {
  const c = price.content || {};
  const raw = region === "uk" ? c.price_uk : c.price_ie;
  const free = isFreePrice(raw, c.category);
  const baseName = (region === "uk" && c.name_uk ? c.name_uk : c.name) || price.name;
  const name = c.asterisk ? `${baseName}${c.asterisk}` : baseName;
  return {
    uuid: price.uuid,
    name,
    cardPrice: free ? "FREE" : formatPrice(raw, region),
    isFree: free,
  };
}

export default function ServicePriceListView({
  title,
  cards,
  pricesByCategory,
}: {
  title?: string;
  cards: CategoryCard[];
  pricesByCategory: Record<string, PriceStory[]>;
}) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const [openUid, setOpenUid] = useState<string | null>(null);

  const cardsWithRows = useMemo(
    () =>
      cards.map((card) => ({
        ...card,
        // Hide prices missing a value for the current region.
        rows: (pricesByCategory[card.category] ?? [])
          .map((p) => buildRow(p, region))
          .filter((row) => Boolean(row.cardPrice)),
      })),
    [cards, pricesByCategory, region],
  );

  // On desktop, expand the consultation-fees card by default. Runs once on
  // the first render where the desktop query matches, via a `didInit` flag.
  const isDesktop = useMediaQuery("(min-width: 1115px)");
  const [didInit, setDidInit] = useState(false);
  if (!didInit && isDesktop) {
    const consultation = cardsWithRows.find(
      (c) => c.category === "consultation_fees",
    );
    setDidInit(true);
    if (
      consultation &&
      (consultation.rows.length > 0 || consultation.footnote)
    ) {
      setOpenUid(consultation.uid);
    }
  }

  if (cardsWithRows.length === 0) return null;

  return (
    <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-10 lg:py-[77px]">
      {title && (
        <h2 className="text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08] text-purple-05">
          {title}
        </h2>
      )}

      <ul className="mt-6 flex flex-col gap-3 md:mt-12 md:gap-4 lg:mt-16 lg:gap-6">
        {cardsWithRows.map((card) => {
          const isOpen = openUid === card.uid;
          const hasRows = card.rows.length > 0;
          const canExpand = hasRows || Boolean(card.footnote);

          return (
            <li
              key={card.uid}
              className="rounded-[8px] bg-white md:rounded-[16px] lg:rounded-[20px]"
            >
              <button
                type="button"
                onClick={(e) => {
                  if (!canExpand) return;
                  const opening = !isOpen;
                  setOpenUid(opening ? card.uid : null);
                  if (opening) {
                    const el = e.currentTarget;
                    setTimeout(
                      () =>
                        el.scrollIntoView({ behavior: "smooth", block: "start" }),
                      320,
                    );
                  }
                }}
                aria-expanded={isOpen}
                disabled={!canExpand}
                className="flex w-full scroll-mt-6 cursor-pointer items-center gap-4 rounded-[8px] p-5 text-left focus-visible:ring-2 focus-visible:ring-purple-03 focus-visible:ring-offset-2 focus-visible:outline-none md:rounded-[16px] md:p-6 lg:rounded-[20px] lg:p-9 disabled:cursor-default"
              >
                <p className="flex-1 text-h5 font-semibold leading-[1.2] text-black md:text-h4 lg:text-h4 lg:leading-[1.08]">
                  {card.label}
                </p>
                {canExpand && (
                  <span className="size-[28px] shrink-0 md:size-[32px] lg:size-[40px]">
                    <PriceToggleIcon open={isOpen} />
                  </span>
                )}
              </button>

              {canExpand && (
                <div
                  inert={!isOpen}
                  className={`grid overflow-hidden rounded-b-[8px] transition-[grid-template-rows] duration-300 ease-out md:rounded-b-[16px] lg:rounded-b-[20px] ${
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    {card.rows.map((row) => (
                      <div
                        key={row.uuid}
                        className="flex items-center justify-between gap-4 border-t border-purple-01 px-5 py-4 md:px-6 md:py-5 lg:pl-9 lg:pr-[54px] lg:py-5"
                      >
                        <p className="text-body leading-[1.56] text-[#595959] lg:text-h6 lg:leading-[1.56]">
                          {row.name}
                        </p>
                        {row.cardPrice && (
                          <p
                            className={`shrink-0 text-body font-bold leading-none lg:text-h6 ${
                              row.isFree ? "text-forest-01" : "text-black"
                            }`}
                          >
                            {row.cardPrice}
                          </p>
                        )}
                      </div>
                    ))}
                    {card.footnote && (
                      <div className="border-t border-purple-01 px-5 py-4 md:px-6 md:py-5 lg:pl-9 lg:pr-[54px] lg:py-5">
                        <p className="text-button leading-[1.56] whitespace-pre-line text-[#595959]">
                          {card.footnote}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
